import { GridList, GridListMasonry, GridListProps } from '@/components/layout/grid';
import { SvgIcon } from '@/components/ui';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { cancelIdle, requestIdle } from '@/utils/idle-callback';
import React, { memo, ReactElement, useEffect, useMemo, useRef } from 'react';
import { Platform, RefreshControl, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useAnimatedScrollHandler } from 'react-native-reanimated';

import { theme } from '@/theme/theme';
import { usePlayerListTemplate } from './context';
import { Empty } from './empty';
import { Separator } from './separator';
import { markSlot } from './slot-marker';

const LIST_TOP_SPACER_FALLBACK_PHONE = 86;

const keyExtractor = <T extends { id: string }>(item: T) => item.id;

export type PlayerListTemplateV2ListProps<T extends { id: string }> = {
  renderItem: (info: { item: T; index: number }) => React.ReactNode;
  numberOfColumns: GridListProps<T>['numColumns'];
  gap: GridListProps<T>['gap'];
  extraData?: GridListProps<T>['extraData'];
  emptyText?: string;
  /** Provide a custom empty component. Overrides `emptyText`. */
  ListEmptyComponent?: ReactElement | React.ComponentType | null;
  /** Provide a custom separator. Pass `false` to disable. */
  ItemSeparatorComponent?: React.ComponentType | false;
  estimatedItemSize?: number;
  recycleItems?: boolean;
  enableLazyLoadingList?: boolean;
  drawDistance?: number;
  waitForInitialLayout?: boolean;
  /** When true (and columns > 1), renders via FlashList masonry. */
  useMasonry?: boolean;
};

function ListComponent<T extends { id: string }>({
  renderItem,
  numberOfColumns,
  gap,
  extraData,
  emptyText,
  ListEmptyComponent,
  ItemSeparatorComponent,
  estimatedItemSize,
  recycleItems,
  enableLazyLoadingList,
  drawDistance,
  waitForInitialLayout,
  useMasonry,
}: PlayerListTemplateV2ListProps<T>) {
  const { scale, isTablet } = useScaling();
  const {
    listLogic,
    scrollY,
    listRef,
    headerActionMeasuredHeight,
    hasActionButton,
    skipHideActionButtonOnScroll,
  } = usePlayerListTemplate<T>();

  const columns = useMemo(() => {
    if (typeof numberOfColumns === 'number') return numberOfColumns;
    return isTablet ? (numberOfColumns?.tablet ?? 1) : (numberOfColumns?.phone ?? 1);
  }, [isTablet, numberOfColumns]);

  const listTopSpacerHeight = isTablet
    ? 0
    : headerActionMeasuredHeight > 0
      ? headerActionMeasuredHeight
      : hasActionButton
        ? scale(LIST_TOP_SPACER_FALLBACK_PHONE)
        : 0;

  const listTopSpacer = useMemo(() => {
    if (isTablet || listTopSpacerHeight <= 0) return null;
    return <View style={{ height: listTopSpacerHeight }} collapsable={false} />;
  }, [isTablet, listTopSpacerHeight]);

  const onScroll = useAnimatedScrollHandler({
    onScroll: event => {
      if (skipHideActionButtonOnScroll) return;
      scrollY.value = event.contentOffset.y;
    },
  });

  const { page, searchText, sort, order, level, lastActive } = listLogic;
  const isLoading = listLogic.isLoading;
  const isFetching = listLogic.isFetching;

  const isFirstScrollRef = useRef(true);

  useEffect(() => {
    if (isLoading || isFetching) return;
    if (isFirstScrollRef.current) {
      isFirstScrollRef.current = false;
      return;
    }
    const handle = requestIdle(() => {
      setTimeout(() => {
        const viewOffset = (() => {
          if (useMasonry) {
            return isTablet ? undefined : 20000;
          }
          return 20000;
        })();
        listRef.current?.scrollToIndex?.({
          index: 0,
          animated: true,
          viewOffset,
        });
      }, 400);
    });
    return () => cancelIdle(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchText, sort, order, level, lastActive, isLoading, isFetching, isTablet]);

  const resolvedEmpty = useMemo<ReactElement | React.ComponentType | null>(() => {
    if (ListEmptyComponent !== undefined) return ListEmptyComponent;
    return <Empty text={emptyText} />;
  }, [ListEmptyComponent, emptyText]);

  const resolvedSeparator = useMemo(() => {
    if (ItemSeparatorComponent === false) return undefined;
    if (ItemSeparatorComponent) return ItemSeparatorComponent;
    return isTablet ? undefined : Separator;
  }, [ItemSeparatorComponent, isTablet]);

  const refreshControl = useMemo(
    () => (
      <RefreshControl
        progressViewOffset={headerActionMeasuredHeight + scale(20)}
        refreshing={false}
        onRefresh={listLogic.refetch}
      />
    ),
    [headerActionMeasuredHeight, listLogic.refetch, scale],
  );

  const commonProps = {
    data: listLogic.listPlayer,
    extraData,
    contentInsetAdjustmentBehavior: (Platform.OS === 'ios' ? 'never' : undefined) as
      | 'never'
      | undefined,
    ListHeaderComponent: listTopSpacer ?? undefined,
    renderItem,
    ListEmptyComponent: resolvedEmpty,
    keyExtractor,
    numColumns: numberOfColumns,
    gap,
    enableLazyLoadingList,
    paddingX: scale({ phone: 0, tablet: 20 }),
    paddingBottom: scale(120),
    bounces: true,
    showsVerticalScrollIndicator: false,
    flex: 1,
    zIndex: 0,
    position: 'relative' as const,
    scrollEventThrottle: 16,
    refreshControl,
  };

  const listNode =
    useMasonry && columns > 1 ? (
      <GridListMasonry
        ref={listRef as never}
        {...commonProps}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
      />
    ) : (
      <GridList
        ref={listRef as never}
        {...commonProps}
        drawDistance={drawDistance}
        recycleItems={recycleItems ?? false}
        ItemSeparatorComponent={resolvedSeparator}
        estimatedItemSize={estimatedItemSize ?? scale(70)}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        onScroll={onScroll}
        waitForInitialLayout={waitForInitialLayout}
      />
    );

  const webRefreshFabStyle = useMemo(
    () => [
      styles.webRefreshFab,
      {
        right: scale(16),
        bottom: scale(82),
        width: scale(50),
        height: scale(50),
        minHeight: scale(44),
        paddingHorizontal: scale(14),
        borderRadius: 8,
      },
    ],
    [scale],
  );

  if (Platform.OS !== 'web') {
    return listNode;
  }

  return (
    <View style={styles.webListShell} pointerEvents="box-none">
      {listNode}
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel="Refresh list"
        activeOpacity={0.85}
        onPress={() => listLogic.refetch()}
        style={webRefreshFabStyle}>
        <SvgIcon name="IcRefresh" size={scale(20)} color={colors.Blue} />
      </TouchableOpacity>
    </View>
  );
}

const MemoList = memo(ListComponent) as typeof ListComponent;
(MemoList as unknown as { displayName: string }).displayName = 'PlayerListTemplateV2.List';

export const List = markSlot(MemoList, 'list') as typeof MemoList;

const styles = StyleSheet.create({
  webListShell: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
  },
  webRefreshFab: {
    position: 'absolute',
    zIndex: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors['Backgrounds/Primary'],
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.18)',
      },
      default: {},
    }),
  },
});
